import { Form, FormInstance, Input } from 'antd'
import { t } from 'i18next'
import { useCallback, useState } from 'react'
import { YesNoSwitch } from '../../../../../../components'
import { AlgorithmRunCompensationMatrixAnalysisConfig } from '../../../../../../store/reducers/workspaceReducer'
import CompensationMatrixAnalysisSelectExperimentRuns from '../../../Components/CompensationMatrixAnalysisSelectExperimentRuns'
import CompensationMatrixAnalysisSelectSsr from '../../../Components/CompensationMatrixAnalysisSelectSsr'
import CompensationMatrixAnalysisSelectSubjectIds from '../../../Components/CompensationMatrixAnalysisSelectSubjectIds'

interface CompensationMatrixAnalysisConfigurationModalStepProps {
  form: FormInstance<AlgorithmRunCompensationMatrixAnalysisConfig>
}

interface CompensationMatrixAnalysisState {
  ssr: string
  manuallySelectExperimentRuns?: boolean
  selectedExperimentRuns?: string[]
  manuallySelectSubjectIds?: boolean
  selectedSubjectIds?: string[]
}

const initialState: CompensationMatrixAnalysisState = {
  ssr: '',
}

const CompensationMatrixAnalysisConfigurationModalStep = ({
  form,
}: CompensationMatrixAnalysisConfigurationModalStepProps) => {
  const [state, setState] = useState(initialState)

  const setFormFields = useCallback(
    (fields: Partial<AlgorithmRunCompensationMatrixAnalysisConfig>) => {
      form.setFieldsValue(fields)
    },
    [form],
  )

  const handleSsrChange = useCallback(
    (value: string) => {
      setFormFields({ ssr: value })
      setState((prev) => ({ ...prev, ssr: value }))
    },
    [setFormFields],
  )

  const handleExperimentRunsChange = useCallback(
    (value: string[]) => {
      setFormFields({ selectedExperimentRuns: value })
      setState((prev) => ({ ...prev, selectedExperimentRuns: value }))
    },
    [setFormFields],
  )

  const handleManuallySelectExperimentRunChange = useCallback(
    (value: boolean) => {
      setFormFields({ manuallySelectExperimentRun: value })
      setState((prev) => ({ ...prev, manuallySelectExperimentRuns: value }))
    },
    [setFormFields],
  )

  return (
    <>
      <Form.Item
        name="ssr"
        label={t(
          'compensationMatrixAnalysisConfigurationModal.selectSsrDescription',
        )}
        rules={[
          {
            required: true,
            message: t(
              'compensationMatrixAnalysisConfigurationModal.selectSsrRequired',
            ),
          },
        ]}
      >
        <CompensationMatrixAnalysisSelectSsr
          value={state.ssr}
          onChange={handleSsrChange}
        />
      </Form.Item>
      <Form.Item
        name="experimentName"
        label={t(
          'compensationMatrixAnalysisConfigurationModal.experimentNameDescription',
        )}
        rules={[
          {
            required: true,
            message: t(
              'compensationMatrixAnalysisConfigurationModal.experimentNameRequired',
            ),
          },
        ]}
      >
        <Input
          placeholder={t(
            'compensationMatrixAnalysisConfigurationModal.experimentNamePlaceholder',
          )}
        />
      </Form.Item>
      <Form.Item
        name="manuallySelectExperimentRuns"
        label={t(
          'compensationMatrixAnalysisConfigurationModal.experimentRunsToggleDescription',
        )}
      >
        <YesNoSwitch
          checked={state.manuallySelectExperimentRuns}
          onClick={handleManuallySelectExperimentRunChange}
        />
      </Form.Item>
      <Form.Item
        name="selectedExperimentRuns"
        label={t(
          'compensationMatrixAnalysisConfigurationModal.selectExperimentRunsDescription',
        )}
      >
        <CompensationMatrixAnalysisSelectExperimentRuns
          value={state.selectedExperimentRuns}
          onChange={handleExperimentRunsChange}
          disabled={!state.manuallySelectExperimentRuns}
        />
      </Form.Item>
      <Form.Item
        name="manuallySelectSubjectIds"
        label={t(
          'compensationMatrixAnalysisConfigurationModal.subjectIdsToggleDescription',
        )}
      >
        <YesNoSwitch
          checked={state.manuallySelectExperimentRuns}
          onClick={handleManuallySelectExperimentRunChange}
        />
      </Form.Item>
      <Form.Item
        name="selectedSubjectIds"
        label={t(
          'compensationMatrixAnalysisConfigurationModal.selectSubjectIdsDescription',
        )}
      >
        <CompensationMatrixAnalysisSelectSubjectIds
          value={state.selectedExperimentRuns}
          onChange={handleExperimentRunsChange}
          disabled={!state.manuallySelectExperimentRuns}
        />
      </Form.Item>
    </>
  )
}

export default CompensationMatrixAnalysisConfigurationModalStep
